import * as React from 'react';

// Externals
import * as _ from 'lodash';
import * as Autosuggest from 'react-autosuggest';
import { Trans, t, Plural } from '@lingui/macro';

// Models
import LocationModel from '../../models/locationModel';

class CitySelectorProps {
  className: string;
  disabled?: boolean;
  selectedValue?: LocationModel;
  excludeLocations: Array<number> = [];
  onSelect: (item: LocationModel) => void;
  zipCodes: Array<LocationModel> = [];
  required?: boolean;
  id?: string;
  loading?: boolean;
}

const CitySelector = (props: CitySelectorProps) => {
  const [suggestions, setSuggestions] = React.useState([]);
  const [text, setText] = React.useState('');

  const getSuggestions = (value) => {
    const inputValue = value ? value.trim().toLowerCase() : '';
    const inputLength = inputValue.length;

    const excludedList = _.differenceWith(
      props.zipCodes,
      props.excludeLocations,
      (location, locationId) => {
        return location.id === locationId;
      }
    );

    return inputLength === 0
      ? []
      : excludedList.filter(
          (zipCode) =>
            zipCode.code.indexOf(inputValue) === 0 ||
            zipCode.name.toLowerCase().indexOf(inputValue) === 0
        );
  };

  const getSuggestionValue = (suggestion) => suggestion.name;

  const renderSuggestion = (suggestion) => (
    <div>
      {suggestion.code} &mdash; {_.capitalize(suggestion.name)}
    </div>
  );

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  const onSuggestionsFetchRequested = ({ value }) => {
    const suggestions = getSuggestions(value);
    setSuggestions(suggestions);
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const inputProps = {
    placeholder: props.loading ? t`Chargement...` : t`Code postal / commune`,
    className: props.className,
    value:
      text ||
      (props.selectedValue &&
        `${props.selectedValue.code} — ${_.capitalize(props.selectedValue.name)}`) ||
      '',
    autoComplete: 'nope',
    disabled: props.disabled ? true : false,
    required: props.required,
    id: props.id,

    onChange: (event, { newValue }) => {
      props.onSelect(null);
      setText(newValue);
    }
  };

  return (
    <Autosuggest
      theme={{
        container: {
          position: 'relative'
        },
        input: {
          paddingTop: '0.3em',
          paddingBottom: '0.3em',
          marginBottom: 20,
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: '#c7d2da',
          borderRadius: 3,
          backgroundColor: '#fff',
          WebkitTransition: ' all 200ms ease-in-out',
          transition: 'all 200ms ease-in-out',
          color: '#263540',
          fontSize: '0.9em',
          lineHeight: '1.5em',
          display: 'block',
          width: '100%',
          maxWidth: 'unset',
          height: 38,
          padding: '8px 12px',
          verticalAlign: 'middle',
          border: '1px solid #cccccc'
        },
        suggestionsContainer: {
          display: 'none'
        },
        suggestionsContainerOpen: {
          color: 'black',
          display: 'block',
          position: 'absolute',
          top: 36,
          width: '100%',
          maxWidth: 450,
          border: '1px solid #aaa',
          backgroundColor: '#fff',
          fontSize: 16,
          lineHeight: 1.25,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          zIndex: 2,
          maxHeight: 260,
          overflowY: 'auto'
        },
        suggestionsList: {
          margin: 0,
          padding: 0,
          listStyleType: 'none'
        },
        suggestion: {
          padding: '10px 20px'
        },
        suggestionHighlighted: {
          backgroundColor: '#ddd'
        }
      }}
      suggestions={suggestions}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={(event, { suggestion }) => {
        setText('');
        props.onSelect(suggestion);
      }}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion}
      inputProps={inputProps}
    />
  );
};

export default CitySelector;
